import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import useWindowSize from '../new-service/useWindowSize';
import NotificationBell from '../notifications/NotificationBell';
import LocationPicker from '../multiLocation/LocationPicker';
import './StaffTopNav.css';

// Reception-mode settings are cached in localStorage and refreshed via a custom
// event when changed (same source the side nav and catalogue tabs read).
const getReceptionSettings = () => {
    try {
        return JSON.parse(localStorage.getItem('receptionModeSettings') || '{}');
    } catch (e) {
        return {};
    }
};

const StaffMembersIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const ScheduleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4" />
        <path d="M8 2v4" />
        <path d="M3 10h18" />
    </svg>
);

const BackArrowIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
    </svg>
);

const PayIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
);

const PermissionsIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
);

const ownerNavItems = [
    { id: 'staffMember', label: 'Team Members', icon: <StaffMembersIcon /> },
    { id: 'schedules', label: 'Schedules & Hours', icon: <ScheduleIcon /> },
    { id: 'staffPay', label: 'Staff Pay', icon: <PayIcon /> },
    { id: 'permissions', label: 'Permissions', icon: <PermissionsIcon /> },
];

const selfEmpNavItems = [
    { id: 'staffMember', label: 'My Profile', icon: <StaffMembersIcon /> },
    { id: 'schedules', label: 'My Rota', icon: <ScheduleIcon /> },
];

function StaffTopNav({ activeTab, onTabChange, children, history, isSelfEmployed, allowedTabs }) {
    const [reception, setReception] = useState(getReceptionSettings);
    useEffect(() => {
        const onChange = (e) => {
            if (e.detail) setReception({ enabled: e.detail.enabled, hiddenAreas: e.detail.hiddenAreas });
        };
        window.addEventListener('receptionModeSettingsChanged', onChange);
        return () => window.removeEventListener('receptionModeSettingsChanged', onChange);
    }, []);

    // Each Team tab maps to a reception-mode key; hide tabs the owner has
    // switched off. Self-employed users only ever see their own profile/rota,
    // so the sub-tab gating doesn't apply to them.
    const tabReceptionKey = { staffMember: 'teamMembers', schedules: 'schedules', staffPay: 'staffPay' };
    const navItems = (isSelfEmployed ? selfEmpNavItems : ownerNavItems).filter((item) => {
        if (isSelfEmployed) return true;
        // Roles & permissions: Staff.teamTabs() says which tabs this person may use.
        if (allowedTabs && allowedTabs[item.id] === false) return false;
        if (!allowedTabs && item.id === 'permissions') return false;
        const key = tabReceptionKey[item.id];
        return !(key && reception?.enabled && reception?.hiddenAreas?.[key]);
    });
    const { width } = useWindowSize();
    const isMobile = width <= 1100;

    const getCurrentTitle = () => {
        const item = navItems.find(n => n.id === activeTab);
        return item ? item.label : 'Team';
    };

    if (isMobile) {
        return (
            <div className="catalogue-mobile-header">
                <button className="catalogue-mobile-back-btn" onClick={() => history.goBack()}>
                    <BackArrowIcon />
                </button>
                <h1 className="catalogue-mobile-title">{getCurrentTitle()}</h1>
                <div className="catalogue-mobile-actions">
                    <LocationPicker slot="mobile" />
                    {children}
                </div>
            </div>
        );
    }

    return (
        <div className="catalogue-top-nav">
            <div className="catalogue-top-nav-container">
                <div className="catalogue-top-nav-content">
                    <nav className="catalogue-tabs-nav">
                        {navItems.map(item => (
                            <button
                                key={item.id}
                                className={`catalogue-tab-btn${activeTab === item.id ? ' active' : ''}`}
                                onClick={() => onTabChange(item.id)}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                    <div className="catalogue-nav-actions">
                        {children}
                    </div>
                </div>
            </div>
            <div className="notif-bell-pinned-wrap">
                <LocationPicker />
                <NotificationBell />
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return { ...state };
}

export default withRouter(connect(mapStateToProps)(StaffTopNav));
